
import { useCallback, useRef } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { Plus, Trash2 } from "lucide-react";
import {
  CustomFieldType,
  type CustomFieldDefinition,
} from "@/types/crm";
import type { DealTypeTemplate } from "@/lib/onboarding/industry-templates";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TagInput } from "@/components/ui/tag-input";
import { SortableList, SortableItem } from "@/components/ui/sortable-list";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DealTypeConfiguratorProps {
  dealTypes: DealTypeTemplate[];
  onChange: (dealTypes: DealTypeTemplate[]) => void;
}

export function DealTypeConfigurator({
  dealTypes,
  onChange,
}: DealTypeConfiguratorProps) {
  // Stable IDs for sortable fields: map dealTypeIndex -> fieldIndex -> id
  const fieldIdsRef = useRef<Map<string, string>>(new Map());
  let _nextId = 0;
  const getFieldId = (dtIndex: number, fIndex: number) => {
    const key = `${dtIndex}-${fIndex}`;
    if (!fieldIdsRef.current.has(key)) {
      fieldIdsRef.current.set(key, `cf-${Date.now()}-${_nextId++}`);
    }
    return fieldIdsRef.current.get(key)!;
  };

  // Rebuild IDs when fields change (after reorder/add/remove)
  const rebuildFieldIds = (dtIndex: number, fields: CustomFieldDefinition[]) => {
    // Clear old IDs for this deal type
    for (const key of [...fieldIdsRef.current.keys()]) {
      if (key.startsWith(`${dtIndex}-`)) {
        fieldIdsRef.current.delete(key);
      }
    }
    // Assign new IDs
    fields.forEach((_, i) => {
      fieldIdsRef.current.set(`${dtIndex}-${i}`, `cf-${Date.now()}-${_nextId++}`);
    });
  };

  const updateDealType = (
    index: number,
    updates: Partial<DealTypeTemplate>
  ) => {
    const updated = [...dealTypes];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeDealType = (index: number) => {
    onChange(dealTypes.filter((_, i) => i !== index));
  };

  const addField = (dealTypeIndex: number) => {
    const updated = [...dealTypes];
    const newFields = [
      ...updated[dealTypeIndex].customFields,
      { name: "", type: CustomFieldType.Text, required: false } as CustomFieldDefinition,
    ];
    updated[dealTypeIndex] = {
      ...updated[dealTypeIndex],
      customFields: newFields,
    };
    rebuildFieldIds(dealTypeIndex, newFields);
    onChange(updated);
  };

  const updateField = (
    dealTypeIndex: number,
    fieldIndex: number,
    updates: Partial<CustomFieldDefinition>
  ) => {
    const updated = [...dealTypes];
    const fields = [...updated[dealTypeIndex].customFields];
    fields[fieldIndex] = { ...fields[fieldIndex], ...updates };
    // Boolean and Checklist fields cannot be required
    if (fields[fieldIndex].type === CustomFieldType.Boolean || fields[fieldIndex].type === CustomFieldType.Checklist) {
      fields[fieldIndex].required = false;
    }
    updated[dealTypeIndex] = {
      ...updated[dealTypeIndex],
      customFields: fields,
    };
    onChange(updated);
  };

  const removeField = (dealTypeIndex: number, fieldIndex: number) => {
    const updated = [...dealTypes];
    const newFields = updated[dealTypeIndex].customFields.filter(
      (_, i) => i !== fieldIndex
    );
    updated[dealTypeIndex] = {
      ...updated[dealTypeIndex],
      customFields: newFields,
    };
    rebuildFieldIds(dealTypeIndex, newFields);
    onChange(updated);
  };

  const reorderFields = useCallback((dealTypeIndex: number, fromIndex: number, toIndex: number) => {
    const updated = [...dealTypes];
    const newFields = arrayMove(updated[dealTypeIndex].customFields, fromIndex, toIndex);
    updated[dealTypeIndex] = {
      ...updated[dealTypeIndex],
      customFields: newFields,
    };
    rebuildFieldIds(dealTypeIndex, newFields);
    onChange(updated);
  }, [dealTypes, onChange]);

  if (dealTypes.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
        All deal types removed. Click &quot;Back&quot; to choose a different
        industry or continue with an empty setup.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
      {dealTypes.map((dealType, dtIndex) => (
        <Card key={dtIndex}>
          <CardHeader className="pb-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-2">
                <Input
                  value={dealType.name}
                  onChange={(e) =>
                    updateDealType(dtIndex, { name: e.target.value })
                  }
                  placeholder="Deal type name"
                  className="font-semibold"
                />
                <Input
                  value={dealType.description}
                  onChange={(e) =>
                    updateDealType(dtIndex, { description: e.target.value })
                  }
                  placeholder="Description (optional)"
                  className="text-sm h-8"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeDealType(dtIndex)}
                className="text-destructive hover:text-destructive shrink-0"
                aria-label="Remove deal type"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                Custom Fields
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => addField(dtIndex)}
              >
                <Plus className="mr-1 h-3 w-3" />
                Add Field
              </Button>
            </div>

            {dealType.customFields.length > 0 && (
              <SortableList
                items={dealType.customFields.map((_, fIndex) => ({ id: getFieldId(dtIndex, fIndex) }))}
                onReorder={(from, to) => reorderFields(dtIndex, from, to)}
              >
              {dealType.customFields.map((field, fIndex) => (
                <SortableItem key={getFieldId(dtIndex, fIndex)} id={getFieldId(dtIndex, fIndex)}>
                <div className="flex min-w-0 items-center gap-2">
                <Input
                  value={field.name}
                  onChange={(e) =>
                    updateField(dtIndex, fIndex, { name: e.target.value })
                  }
                  placeholder="Field name"
                  className="h-8 min-w-0 flex-1"
                />
                <Select
                  value={field.type}
                  onValueChange={(value) =>
                    updateField(dtIndex, fIndex, {
                      type: value as CustomFieldType,
                      options:
                        value === CustomFieldType.Select || value === CustomFieldType.Multiselect
                          ? field.options || []
                          : undefined,
                      ...(value === CustomFieldType.Boolean || value === CustomFieldType.Checklist ? { required: false } : {}),
                    })
                  }
                >
                  <SelectTrigger className="h-8 w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CustomFieldType.Text}>Text</SelectItem>
                    <SelectItem value={CustomFieldType.Textarea}>Textarea</SelectItem>
                    <SelectItem value={CustomFieldType.Number}>
                      Number
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Date}>Date</SelectItem>
                    <SelectItem value={CustomFieldType.Boolean}>
                      Boolean
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Select}>
                      Select
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Multiselect}>
                      Multiselect
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Checklist}>
                      Checklist
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Member}>
                      Member
                    </SelectItem>
                    <SelectItem value={CustomFieldType.Members}>
                      Members
                    </SelectItem>
                  </SelectContent>
                </Select>
                <label className={cn(
                  "flex items-center gap-1 shrink-0",
                  field.type === CustomFieldType.Boolean || field.type === CustomFieldType.Checklist ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                )}>
                  <input
                    type="checkbox"
                    checked={field.required || false}
                    onChange={(e) =>
                      updateField(dtIndex, fIndex, {
                        required: e.target.checked,
                      })
                    }
                    disabled={field.type === CustomFieldType.Boolean || field.type === CustomFieldType.Checklist}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs">Req</span>
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => removeField(dtIndex, fIndex)}
                  aria-label="Remove field"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
                </div>
                </SortableItem>
              ))}
              </SortableList>
            )}

            {dealType.customFields.some(
              (f) => f.type === CustomFieldType.Select || f.type === CustomFieldType.Multiselect
            ) && (
              <div className="space-y-2 pt-1">
                {dealType.customFields.map((field, fIndex) =>
                  field.type === CustomFieldType.Select || field.type === CustomFieldType.Multiselect ? (
                    <div
                      key={fIndex}
                      className="pl-2 border-l-2 border-muted"
                    >
                      <Label className="text-xs text-muted-foreground">
                        {field.name || (field.type === CustomFieldType.Multiselect ? "Multiselect field" : "Select field")} options
                      </Label>
                      <TagInput
                        values={field.options || []}
                        onChange={(options) =>
                          updateField(dtIndex, fIndex, { options })
                        }
                        placeholder="Type option and press Enter"
                      />
                    </div>
                  ) : null
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
